import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';
import { useHandleActionItem } from '@/components/hooks/useHandleActionItem';
import { useCoreContext } from '@/core';
import { PermissionEntity } from '@/domain/entities/permission';
import { RolesEntity } from '@/domain/entities/roles';
import { RolesPayLoad } from '@/domain/ports/roles/payloads';
import { UpdateRolesUseCase, CreateRolesUseCase } from '@/domain/use-cases/roles';
import yup from '@/yupGlobal';
import { yupResolver } from '@hookform/resolvers/yup';
import { notification } from 'antd';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { RolesRepoIml } from '../adapters/repositories';

interface Props {
  detailRole: RolesEntity;
  openForm: boolean;
  showId: string | null;
  listPermission?: PermissionEntity[];
}

const useRolesForm = (props: Props) => {
  const { detailRole, openForm, listPermission } = props;
  const { handleClose } = useHandleActionItem();
  const { actionGetAll } = useCoreContext();
  const [currentQueryParameters] = useSearchParams();
  const showId = currentQueryParameters.get('id');
  const action = currentQueryParameters.get('action');
  const isDuplicate = action === 'Duplicate';
  const createRoles = new CreateRolesUseCase(new RolesRepoIml());
  const updateRoles = new UpdateRolesUseCase(new RolesRepoIml());

  const formatRoles = useCallback(() => {
    if (showId && Array.isArray(detailRole.permissions) && detailRole.permissions.length > 0) {
      return detailRole.permissions.map((item) => {
        return {
          app: item.app,
          permissions: item.permissions,
        };
      });
    } else {
      if (Array.isArray(listPermission) && listPermission.length > 0) {
        const baseArr = listPermission.map((item) => {
          return {
            app: item.app,
            permissions: [],
          };
        });
        return baseArr;
      }
      return [];
    }
  }, [showId, openForm, listPermission, detailRole]);

  const defaultValue = {
    id: '',
    name: '',
    description: '',
    permissions: formatRoles(),
    isActive: true,
  };

  const getFormDefaultValues = (): RolesPayLoad => {
    if (isDuplicate) {
      return {
        id: '',
        name: '',
        description: '',
        permissions: formatRoles(),
        isActive: detailRole.isActive ?? true,
      };
    }

    return {
      id: detailRole.id ?? '',
      name: detailRole.name ?? '',
      description: detailRole.description ?? '',
      permissions: formatRoles(),
      isActive: detailRole.isActive ?? true,
    };
  };

  const formDefaultValues = getFormDefaultValues();

  const methodForm = useForm<RolesPayLoad>({
    mode: 'onTouched',
    defaultValues: formDefaultValues,
    resolver: yupResolver(
      yup.object({
        name: yup.string().required('Role Name is required'),
        description: yup.string().required('Description is required'),
      }),
    ),
  });

  const onSubmit = methodForm.handleSubmit(async (data: RolesPayLoad) => {
    try {
      const sanitizedPermissions = (data.permissions ?? []).map((item) => {
        return {
          app: item.app,
          permissions: item.permissions,
        };
      });

      if (!showId || isDuplicate) {
        const createPayload: RolesPayLoad = {
          name: data.name,
          description: data.description,
          isActive: data.isActive,
          permissions: sanitizedPermissions,
        };
        await createRoles.create(createPayload);
        notification.success({
          message: 'Role created successfully',
          placement: 'bottomRight',
        });
        await actionGetAll();
        handleClose();
        methodForm.reset({
          ...defaultValue,
        });
      } else {
        const getDataRoleUpdate = {
          ...data,
          permissions: sanitizedPermissions,
        };
        await updateRoles.update(getDataRoleUpdate);
        notification.success({
          message: 'Role updated successfully',
          placement: 'bottomRight',
        });
        await actionGetAll();
        handleClose();
        methodForm.reset({
          ...defaultValue,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: getErrorMessage(error),
        placement: 'bottomRight',
      });
    }
  });

  return {
    methodForm,
    onSubmit,
    defaultValue,
    isDuplicate,
  };
};

export default useRolesForm;
